import React, { useState, useEffect,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import "./ConclusionCartographieSite.css";
import { saveDossier } from "../../redux/dispatch";
import SaveLoader from "../shared/SaveLoader";
import toast from 'react-hot-toast';
import useConfidence from "../../hooks/useConfidence";
import ConfidenceLabel from "../shared/ConfidenceLabel";

import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";
const RUBRIQUE = "conclusion_cartographie_site";

const INITIAL_FORM = {
  diagnostic: "",
  site_primitif: "",
  stade: "",
  etat_nutritionnel: "",
  autres_morbidites: "",
};

const ROWS = [
  { key: "diagnostic",        label: "Diagnostic" },
  { key: "site_primitif",     label: "Site primitif / Latéralité\nAtteinte des relais ganglionnaires" },
  { key: "stade",             label: "Stade" },
  { key: "etat_nutritionnel", label: "État nutritionnel au diagnostic" },
  { key: "autres_morbidites", label: "Autres morbidités cliniques et/ou biologiques associées" },
];

const ConclusionCartographieSite = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];
  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;
  const formulairesData  = useSelector((s) => s.formulaires.data);
  const savedData = useSelector(
    (s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.[RUBRIQUE] || {}
  );
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_FORM, ...savedData });

  const formRef = useRef(form);

  useEffect(() => {
    setForm({ ...INITIAL_FORM, ...savedData });
  }, [id]);

  useEffect(() => { formRef.current = form; }, [form]);

  const [imgIndex, setImgIndex] = useState(0);


  // APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav("conclusion_cartographie_site");

  // ── Confidence ──────────────────────────────────────────────────────────────
  const { getConfidence } = useConfidence(id, RUBRIQUE);
  const gc = (field) => getConfidence(RUBRIQUE, field);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, RUBRIQUE, formRef.current));
  };

    useEffect(() => {
      return () => {
        handleSave();
      };
    }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, RUBRIQUE, form));
    
   try {
    const result = await dispatch(saveDossier());
    toast.success(result.message);
    navigate("/");
  } catch (err) {
    toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
  }
    finally {
    setSaving(false);
  }
  };

  const handleCancel = () => {
    setForm({ ...INITIAL_FORM, ...savedData });
  };

  if (!dossier) {
    return (
      <div className="ccs-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="ccs-page">
       {saving && <SaveLoader />}
      <header className="ccs-header">
        <div className="ccs-header-left">
          <button className="ccs-back-btn" onClick={() => navigate("/")}>
            ← Retour à la liste
          </button>
          <div>
            <h1 className="ccs-title">Dossier {dossier.numero_dossier}</h1>
            <p className="ccs-subtitle">
              Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}
            </p>
          </div>
        </div>
        <button className="ccs-save-btn" onClick={handleSaveAll} disabled={saving}>
          Sauvegarder les modifications
        </button>
      </header>

      <div className="ccs-body">
        {/* Colonne gauche : image */}
        <aside className="ccs-image-panel">
          <div className="ccs-image-header">
            <span className="ccs-rubrique-label">Conclusion – Cartographie des Sites</span>
            {IMAGES.length > 0 && (
              <span className="ccs-image-counter">
                Image {imgIndex + 1} / {IMAGES.length}
              </span>
            )}
          </div>

          <div className="ccs-image-viewer">
            {IMAGES.length > 0 ? (
              <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="ccs-image" />
            ) : (
              <div className="ccs-image-placeholder">
                <span>Aucune image disponible</span>
              </div>
            )}
          </div>

          {IMAGES.length > 1 && (
            <div className="ccs-image-nav">
              <button className="ccs-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
              <div className="ccs-image-dots">
                {IMAGES.map((_, i) => (
                  <span key={i} className={`ccs-dot ${i === imgIndex ? "ccs-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
                ))}
              </div>
              <button className="ccs-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
            </div>
          )}
        </aside>

        {/* Colonne droite : formulaire */}
        <main className="ccs-form-panel">
          <div className="ccs-form-scroll">
            <section className="ccs-section">
              <h2 className="ccs-section-title">Conclusion</h2>
              <div className="ccs-conclusion-table">
                {ROWS.map(({ key, label }) => (
                  <div key={key} className="ccs-conclusion-row">
                    <div className="ccs-conclusion-label">
                      <span>
                        {label.split("\n").map((line, i, arr) => (
                          <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                        ))}
                      </span>
                      <br />
                      <ConfidenceLabel confidence={gc(key)} />
                    </div>
                    <div className="ccs-conclusion-value">
                      <textarea
                        rows={3}
                        placeholder="—"
                        value={form[key] ?? ""}
                        onChange={(e) => handleChange(key, e.target.value)}
                        style={{ borderColor: gc(key).color || undefined }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

         {/*  <div className="ccs-form-actions">
            <button className="ccs-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="ccs-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />
        </main>
      </div>
    </div>
  );
};

export default ConclusionCartographieSite;